import type { ButtonHTMLAttributes, ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant
  size?: Size
  loading?: boolean
  iconStart?: ReactNode
  iconEnd?: ReactNode
  fullWidth?: boolean
  children?: ReactNode
}

/**
 * Button — the action primitive.
 *
 * A native <button>, always. A styled <div role="button"> loses Space/Enter
 * activation, form submission, and the disabled semantics, and every one of
 * those has to be reimplemented worse.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  iconStart,
  iconEnd,
  fullWidth = false,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'lg-btn',
    `lg-btn--${variant}`,
    size !== 'md' && `lg-btn--${size}`,
    fullWidth && 'lg-btn--full',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      // aria-disabled rather than the disabled attribute while loading: a
      // disabled element is removed from the tab order, so a screen reader
      // user who triggered the action loses focus and any announcement of
      // what happened.
      disabled={disabled}
      aria-disabled={loading || disabled || undefined}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : rest.onClick}
    >
      {/* The spinner replaces the leading icon rather than being appended, so
          the button keeps its width. A button that shrinks to a spinner moves
          out from under the pointer the instant it is clicked. */}
      {loading ? (
        <span className="lg-btn__spinner" aria-hidden="true" />
      ) : (
        iconStart && <span aria-hidden="true">{iconStart}</span>
      )}
      {children}
      {iconEnd && !loading && <span aria-hidden="true">{iconEnd}</span>}
      {loading && <span className="lg-sr-only">Working…</span>}
    </button>
  )
}
